import { useMemo, useState } from "react";
import type { Color, OrderableItem } from "../../api/types";
import { eur } from "../bouquet/builderShared";
import { ItemThumb } from "../ItemThumb";
import {
  goodsTotal,
  retailLineFromItem,
  retailTotal,
  type ComposerCart,
  type RetailOrderLine,
} from "./orderBuild";

interface Props {
  items: OrderableItem[];
  colors: Color[];
  lines: RetailOrderLine[];
  onChange(lines: RetailOrderLine[]): void;
  bouquet: ComposerCart;
  referencePhotoCount: number;
  submitting: boolean;
  error: string | null;
  submitLabel: string;
  totalEur: string;
  onTotalEurChange(value: string): void;
  onBack(): void;
  onSubmit(): void;
}

export function PhoneOrderItems({
  items,
  colors,
  lines,
  onChange,
  bouquet,
  referencePhotoCount,
  submitting,
  error,
  submitLabel,
  totalEur,
  onTotalEurChange,
  onBack,
  onSubmit,
}: Props) {
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [colorId, setColorId] = useState("");
  /*
   * G-93: the catalogue moved into a sheet, the way the bouquet composer's did.
   * Nineteen photo cards used to sit between the summary and the cart, so on a
   * phone you scrolled the whole sellable catalogue to see the two things you
   * had picked — and scrolled back down to change a quantity.
   */
  const [pickerOpen, setPickerOpen] = useState(false);

  const categories = useMemo(() => {
    const byId = new Map(items.map((item) => [item.category.id, item.category]));
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (categoryId && item.category.id !== categoryId) return false;
      if (colorId && item.color?.id !== colorId) return false;
      if (query && !item.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [items, search, categoryId, colorId]);

  const add = (item: OrderableItem) => {
    const existing = lines.find((line) => line.itemId === item.id);
    if (existing) {
      if (existing.qty >= existing.maxQty) return;
      onChange(
        lines.map((line) =>
          line.itemId === item.id ? { ...line, qty: line.qty + 1 } : line,
        ),
      );
      return;
    }
    if (Number(item.qtyOnHand) <= 0) return;
    onChange([...lines, retailLineFromItem(item)]);
  };

  const step = (itemId: string, delta: number) =>
    onChange(
      lines
        .map((line) =>
          line.itemId === itemId
            ? {
                ...line,
                qty: Math.max(0, Math.min(line.maxQty, line.qty + delta)),
              }
            : line,
        )
        .filter((line) => line.qty > 0),
    );

  const bouquetCount = bouquet.flowers.length + bouquet.papers.length;
  /*
   * A described bouquet carries text instead of component lines. TEXT, and not
   * a reference photo: `submit()` accepts a lineless order only when the notes
   * or the name carry something (OrderBuildPage's `describeTextPresent`, which
   * mirrors what the server accepts). Counting a photo here would let this line
   * promise a bouquet the Send button then refuses — the same contradiction
   * this wording was written to end, one gate further along.
   */
  const describedBouquet =
    bouquetCount === 0 &&
    (bouquet.bouquetNotes.trim().length > 0 || bouquet.bouquetName.trim().length > 0);
  const itemsTotal = retailTotal(lines);
  const bouquetSuggested = bouquet.flowers.reduce(
    (sum, line) => sum + (line.unitSalePrice ?? 0) * line.qty,
    0,
  );
  const bouquetPrice = totalEur.trim() ? Number(totalEur) : null;
  const goods = goodsTotal(totalEur, lines);

  return (
    <div className="pob-items-step">
      <div className="card pob-order-summary">
        <h3>Order summary</h3>
        {/* G-23 step order is bouquet → items → customer & delivery, so the
            customer and the delivery leg are not known yet on this step —
            the full customer-pays breakdown lives on the next one
            (pre-release fix 2026-09-09; this card used to read the still
            empty checkout form and said "Customer · Store pickup"). */}
        {/*
          * G-93: this read "No bouquet" for a DESCRIBED one, which has no
          * component lines by definition — the florist composes it in the shop.
          * Saying "none" about a bouquet the person just wrote a paragraph
          * about is the page contradicting them.
          */}
        <p>
          {bouquetCount > 0
            ? `${bouquetCount} bouquet ${bouquetCount === 1 ? "component" : "components"}`
            : describedBouquet
              ? "Bouquet described for the florist"
              : "No bouquet"}{" "}
          · {lines.length} shop {lines.length === 1 ? "item" : "items"}
        </p>
        <p className="muted">Customer and delivery come on the next step.</p>
        {bouquet.bouquetName ? <p><strong>{bouquet.bouquetName}</strong></p> : null}
        {bouquet.bouquetNotes ? <p className="muted">{bouquet.bouquetNotes}</p> : null}
        {referencePhotoCount > 0 ? (
          <p className="muted">
            {referencePhotoCount === 1
              ? "1 reference photo attached"
              : `${referencePhotoCount} reference photos attached`}
          </p>
        ) : null}
        {lines.length === 0 ? (
          <p className="muted">
            Shop items are optional. Send now for a bouquet-only order.
          </p>
        ) : null}

        <div className="field pob-total-field">
          <label htmlFor="pob-final-total">Agreed bouquet price €</label>
          <input
            id="pob-final-total"
            type="number"
            min={0}
            step="0.01"
            value={totalEur}
            onChange={(event) => onTotalEurChange(event.target.value)}
            placeholder={bouquetSuggested > 0 ? bouquetSuggested.toFixed(2) : "0.00"}
          />
          {bouquetSuggested > 0 ? (
            <small className="hint">Suggested {eur(bouquetSuggested)}</small>
          ) : null}
        </div>

        {/*
          G-15 (owner, 2026-07-31): the order stores the GOODS total — bouquet
          + shop items. Delivery is priced on the delivery job (it drives the
          courier payout), so it is shown here for the customer conversation
          but never folded into the order total the register rings up.
        */}
        <dl className="pob-price-breakdown">
          <div>
            <dt>Bouquet</dt>
            <dd>{bouquetPrice != null ? eur(bouquetPrice) : "not quoted"}</dd>
          </div>
          {lines.length > 0 ? (
            <div>
              <dt>
                Shop items ({lines.length})
              </dt>
              <dd>{eur(itemsTotal)}</dd>
            </div>
          ) : null}
          <div className="pob-price-breakdown-sum">
            <dt>Order total (goods)</dt>
            <dd>{goods != null ? eur(goods) : "—"}</dd>
          </div>
        </dl>
      </div>

      <div className="card pob-retail-cart">
        <div className="pob-built-head">
          <span>Items in this order</span>
          <span>
            {lines.length === 0
              ? "none"
              : `${lines.reduce((n, l) => n + l.qty, 0)} · ${eur(itemsTotal)}`}
          </span>
        </div>

        {lines.length === 0 ? (
          <p className="muted pob-built-empty">
            Nothing added. Candles, vases and toys live behind the button below.
          </p>
        ) : (
          lines.map((line) => (
            <div key={line.itemId} className="pob-cart-row">
              <span className="pob-cart-row-name">{line.name}</span>
              <div className="pob-qty-stepper">
                <button
                  type="button"
                  aria-label={`One fewer ${line.name}`}
                  onClick={() => step(line.itemId, -1)}
                >
                  −
                </button>
                <span>{line.qty}</span>
                <button
                  type="button"
                  aria-label={`One more ${line.name}`}
                  disabled={line.qty >= line.maxQty}
                  onClick={() => step(line.itemId, 1)}
                >
                  +
                </button>
              </div>
              <span className="pob-cart-row-money">
                {line.unitSalePrice != null ? eur(line.unitSalePrice * line.qty) : "—"}
              </span>
              {line.unavailable ? (
                <span className="badge danger">currently unavailable</span>
              ) : null}
            </div>
          ))
        )}

        <button
          type="button"
          className="button tinted pob-add-btn"
          onClick={() => setPickerOpen(true)}
        >
          Add items
        </button>
      </div>

      {pickerOpen ? (
        <div className="pob-sheet-backdrop" role="dialog" aria-label="Add shop items">
          <div className="pob-sheet pob-picker-sheet">
            <div className="pob-picker-head">
              <h3 className="pob-sheet-title">Add items</h3>
              <button
                type="button"
                className="button plain"
                onClick={() => setPickerOpen(false)}
              >
                Done
              </button>
            </div>

            <input
              type="search"
              className="pob-search"
              placeholder="Search items by name…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <div className="pob-chip-row" aria-label="Item category">
              <button
                type="button"
                className={`sf-type-chip${categoryId ? "" : " sf-type-chip--active"}`}
                onClick={() => setCategoryId("")}
              >
                All categories
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`sf-type-chip${categoryId === category.id ? " sf-type-chip--active" : ""}`}
                  onClick={() => setCategoryId(categoryId === category.id ? "" : category.id)}
                >
                  {category.label}
                </button>
              ))}
            </div>

            <div className="pob-chip-row" aria-label="Item color">
              <button
                type="button"
                className={`sf-color-chip${colorId ? "" : " sf-color-chip--active"}`}
                onClick={() => setColorId("")}
              >
                All colors
              </button>
              {colors.map((color) => (
                <button
                  key={color.id}
                  type="button"
                  className={`sf-color-chip${colorId === color.id ? " sf-color-chip--active" : ""}`}
                  onClick={() => setColorId(colorId === color.id ? "" : color.id)}
                >
                  <span className="pob-color-dot" style={{ background: color.hex ?? "#bbb" }} />
                  {color.name}
                </button>
              ))}
            </div>

            {/*
              * The list scrolls, the search and chips above it do not: this is
              * the wrapper `.pob-picker-list` exists for. Dropping the grid
              * straight into the flex column let it shrink the chip rows to
              * 8 px — both filter rows overlapped into one unreadable band.
              */}
            <div className="pob-picker-list">
              {visible.length === 0 ? (
                <div className="empty-state">No shop items match these filters.</div>
              ) : (
                <div className="pob-grid">
                  {visible.map((item) => {
                    const selected = lines.find((line) => line.itemId === item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`pob-card${selected ? " pob-card--selected" : ""}`}
                        onClick={() => add(item)}
                      >
                        <div className="pob-card-photo">
                          {selected ? <span className="pob-card-count">{selected.qty}</span> : null}
                          <ItemThumb itemId={item.id} imageKey={item.imageKey} alt={item.name} size={92} />
                        </div>
                        <span className="pob-card-name">{item.name}</span>
                        <span className="pob-card-price">
                          {item.defaultSaleUnitPriceEur
                            ? eur(Number(item.defaultSaleUnitPriceEur))
                            : "Price missing"}
                        </span>
                        <span className="pob-card-stock">
                          {item.qtyOnHand} in stock · {item.category.label}
                          {item.color ? ` · ${item.color.name}` : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}
      <div className="button-row pob-checkout-actions">
        <button type="button" className="button plain" onClick={onBack} disabled={submitting}>
          Back to bouquet
        </button>
        <button type="button" className="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}
